import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const SearchableDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Search or select...", 
  label,
  id,
  required = false,
  disabled = false,
  allowCustomInput = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      // If allowCustomInput is true and the search term doesn't exactly match any option,
      // add a custom option
      if (allowCustomInput && searchTerm && !filtered.some(option => 
        option.toLowerCase() === searchTerm.toLowerCase()
      )) {
        filtered.push(`Custom: ${searchTerm}`);
      }
      
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, allowCustomInput]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Only reset searchTerm if the current value is not a valid option and custom input is not allowed
        // This prevents clearing the selected value when clicking on other dropdowns
        if (searchTerm && !options.includes(searchTerm) && !allowCustomInput) {
          setSearchTerm(value || '');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchTerm, options, value, allowCustomInput]);

  // Update search term when value changes externally
  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onChange(newValue);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionSelect = (option) => {
    // Handle custom options by extracting the actual value
    const actualValue = option.startsWith('Custom: ') ? option.replace('Custom: ', '') : option;
    setSearchTerm(actualValue);
    onChange(actualValue);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        inputRef.current?.focus();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0 && searchTerm) {
        // If there's an exact match, select it
        const exactMatch = filteredOptions.find(option => 
          option.toLowerCase() === searchTerm.toLowerCase()
        );
        if (exactMatch) {
          handleOptionSelect(exactMatch);
        } else {
          // Select the first filtered option
          handleOptionSelect(filteredOptions[0]);
        }
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm(value || '');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
          } ${required && !value ? 'border-red-300' : ''}`}
          required={required}
          autoComplete="off"
        />
        
        <button
          type="button"
          onClick={handleToggleDropdown}
          disabled={disabled}
          className={`absolute inset-y-0 right-0 flex items-center px-2 ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
          tabIndex={-1}
        >
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-gray-500 text-sm">
              No options found
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <button
                key={`${option}-${index}`}
                type="button"
                onClick={() => handleOptionSelect(option)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                  value === option ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                }`}
              >
                {option}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
